import React from 'react';
import { Plus, Workflow } from 'lucide-react';
import PageShell from '../../components/PageShell';
import { APP_NAME } from '../../config/region';
import {
  Button,
  IconButton,
  Pill,
  Card,
  EmptyState,
  Input,
  Textarea,
  Select,
  Checkbox,
  Radio,
  Tabs,
  Menu,
  type PillIntent,
  type ButtonIntent,
  type ButtonSize,
  type TabItem,
} from '../../ui';

// LT-308 · DS v1 showcase. Renders every component × every variant.
// Mounted at /dev/design-system. Used by designers + devs to verify the system.

const pillIntents: PillIntent[] = [
  'accent',
  'ok',
  'warn',
  'danger',
  'info',
  'neutral',
];

const buttonIntents: ButtonIntent[] = [
  'primary',
  'secondary',
  'ghost',
  'danger',
];

const buttonSizes: ButtonSize[] = ['sm', 'md', 'lg'];

const tabItems: TabItem[] = [
  { value: 'templates', label: 'Templates' },
  { value: 'fields', label: 'Custom fields' },
  { value: 'booking', label: 'Booking pages' },
  { value: 'calendars', label: 'Calendars' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'senders', label: 'Senders' },
];

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mb-12">
    <h2 className="text-[11px] font-mono tracking-widest uppercase text-accent mb-3">
      {title}
    </h2>
    <div className="flex flex-col gap-4">{children}</div>
  </section>
);

const DesignSystemPage: React.FC = () => {
  const [tabValue, setTabValue] = React.useState<string>('templates');

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto py-6">
        <header className="mb-10">
          <div className="text-[11px] font-mono tracking-widest uppercase text-slate-400 mb-2">
            Design System · v1 · LT-308
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-50">
            {APP_NAME} DS v1
          </h1>
          <p className="text-slate-300 max-w-2xl mt-2 text-sm">
            Every component × every variant. See{' '}
            <code className="font-mono text-accent-text">
              frontend/src/ui/README.md
            </code>{' '}
            for adoption rules.
          </p>
        </header>

        <Section title="01 · Buttons">
          {buttonIntents.map((intent) => (
            <Card key={intent} padding="default">
              <h3 className="text-sm font-semibold mb-3 capitalize text-slate-50">
                {intent}
              </h3>
              <div className="flex flex-wrap gap-3 items-center">
                {buttonSizes.map((size) => (
                  <Button key={size} intent={intent} size={size}>
                    {intent} {size}
                  </Button>
                ))}
                <Button intent={intent} disabled>
                  Disabled
                </Button>
              </div>
            </Card>
          ))}
        </Section>

        <Section title="02 · IconButtons">
          <Card>
            <div className="flex flex-wrap gap-3 items-center">
              <IconButton aria-label="Call contact">
                <svg
                  width={14}
                  height={14}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </IconButton>
              <IconButton aria-label="Send email" intent="secondary">
                <svg
                  width={14}
                  height={14}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </IconButton>
              <IconButton aria-label="Delete" intent="danger">
                <svg
                  width={14}
                  height={14}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                </svg>
              </IconButton>
            </div>
          </Card>
        </Section>

        <Section title="03 · Pills · status system">
          <Card>
            <div className="flex flex-wrap gap-2">
              {pillIntents.map((intent) => (
                <Pill key={intent} intent={intent}>
                  {intent}
                </Pill>
              ))}
            </div>
          </Card>
        </Section>

        <Section title="04 · Cards · padding × elevation">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card padding="tight">
              <h3 className="font-semibold text-slate-50 text-sm mb-1">
                Tight
              </h3>
              <p className="text-slate-400 text-xs">padding=tight · p-3</p>
            </Card>
            <Card padding="default">
              <h3 className="font-semibold text-slate-50 text-sm mb-1">
                Default
              </h3>
              <p className="text-slate-400 text-xs">padding=default · p-5</p>
            </Card>
            <Card padding="loose" elevation="glow">
              <h3 className="font-semibold text-slate-50 text-sm mb-1">
                Loose · glow
              </h3>
              <p className="text-slate-400 text-xs">elevation=glow</p>
            </Card>
          </div>
        </Section>

        <Section title="05 · Inputs · Textarea · Select">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Search"
              placeholder="Name, email, or phone"
              defaultValue=""
            />
            <Input
              label="Email"
              defaultValue="bad-email"
              error="Enter a valid email address"
            />
            <Input label="Disabled" defaultValue="Nothing here" disabled />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Textarea
              label="Notes"
              placeholder="Anything relevant…"
              hint="Supports multiple lines."
            />
            <Select label="Lifecycle" defaultValue="any">
              <option value="any">Any stage</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
            </Select>
          </div>
        </Section>

        <Section title="06 · Checkbox · Radio">
          <Card>
            <div className="flex flex-col gap-3">
              <Checkbox label="I agree to the terms" />
              <Checkbox
                label="Email me updates"
                hint="No more than one digest per week."
                defaultChecked
              />
              <Checkbox label="Disabled option" disabled />
              <div className="h-px bg-white/[0.08]" />
              <Radio
                name="demo-role"
                value="admin"
                label="Admin"
                defaultChecked
              />
              <Radio name="demo-role" value="member" label="Member" />
              <Radio
                name="demo-role"
                value="guest"
                label="Guest"
                hint="Read-only access"
              />
            </div>
          </Card>
        </Section>

        <Section title="07 · Tabs">
          <Card>
            <Tabs
              items={tabItems}
              value={tabValue}
              onChange={setTabValue}
              aria-label="Settings sections (demo)"
            />
            <div className="p-3 text-slate-300 text-sm">
              Viewing: <b>{tabValue}</b>
            </div>
          </Card>
        </Section>

        <Section title="08 · EmptyState · first-run / zero data">
          <EmptyState
            surface="design_system_demo"
            icon={Workflow}
            headline="Build your first sequence"
            subhead="A sequence is a multi-step flow — call, wait, email, branch."
            primary={{ label: 'Start from scratch', icon: Plus, onClick: () => {} }}
            secondary={{ label: 'Pick a template', onClick: () => {} }}
          />
        </Section>

        <Section title="09 · Menu (keyboard-navigable)">
          <Card>
            <div className="flex gap-8 flex-wrap">
              <Menu
                aria-label="Contact actions (demo)"
                items={[
                  { label: 'Call contact', kbd: 'C', onSelect: () => {} },
                  { label: 'Send email', kbd: 'E', onSelect: () => {} },
                  { label: 'Send SMS', kbd: 'S', onSelect: () => {} },
                  'sep',
                  { label: 'Add to sequence…', onSelect: () => {} },
                  { label: 'Copy phone number', onSelect: () => {} },
                  'sep',
                  {
                    label: 'Delete contact',
                    onSelect: () => {},
                    danger: true,
                  },
                ]}
              />
            </div>
          </Card>
        </Section>
      </div>
    </PageShell>
  );
};

export default DesignSystemPage;
